import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import authService from '../services/authService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageHeader, PasswordStrength, SuccessCheck } from '../components/ui';

const ChangePassword: React.FC = () => {
  usePageTitle('Change Password');
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.changePassword({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });
      toast.success('Password updated');
      setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader title="Change Password" subtitle="Update your account password" />

      <div className="max-w-md animate-fade-up stagger-2">
        <div className="surface-card p-6" style={{ borderTop: '3px solid var(--card-accent-indigo)' }}>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="cp-oldPassword" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Current Password
              </label>
              <input
                id="cp-oldPassword"
                type="password"
                required
                value={formData.oldPassword}
                onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
                className="w-full p-2 border rounded-lg input-themed"
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="cp-newPassword" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                New Password
              </label>
              <input
                id="cp-newPassword"
                type="password"
                required
                minLength={6}
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className="w-full p-2 border rounded-lg input-themed"
                disabled={isSubmitting}
              />
              <PasswordStrength password={formData.newPassword} />
              {!formData.newPassword && (
                <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>Minimum 6 characters</p>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="cp-confirmPassword" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Confirm New Password
              </label>
              <input
                id="cp-confirmPassword"
                type="password"
                required
                minLength={6}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full p-2 border rounded-lg input-themed"
                disabled={isSubmitting}
              />
              {formData.confirmPassword && formData.newPassword && (
                <p className="mt-1 text-xs font-medium" style={{
                  color: formData.confirmPassword === formData.newPassword
                    ? 'var(--badge-green-text)'
                    : 'var(--badge-red-text)',
                }}>
                  {formData.confirmPassword === formData.newPassword ? 'Passwords match' : 'Passwords don\u2019t match'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || showSuccess}
              className="btn-primary w-full py-2.5 px-4 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {showSuccess ? (
                <span className="inline-flex items-center gap-2">
                  <SuccessCheck size={18} /> Done
                </span>
              ) : isSubmitting ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
